import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { usePasswordReset } from '../../hooks/usePasswordReset';
import { validatePassword } from '../../utils/passwordValidation';
import './PasswordResetPage.scss';

// Asset imports
import accoladeLogo from '../../assets/Logo-Accolade-dark.svg';
import brandLogos from '../../assets/Tri-Brand_Maroline-logos-dark.svg';
import languageIcon from '../../assets/icons/language-icon-gray.svg';
import showPasswordIcon from '../../assets/icons/show-password-icon.svg';

export const PasswordResetPage: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const { handleResetPassword, error: resetError, isLoading: resetLoading } = usePasswordReset();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const isFormValid = formData.newPassword && 
    formData.confirmPassword && 
    formData.newPassword === formData.confirmPassword && 
    validationErrors.length === 0;

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'newPassword') {
      const { errors } = validatePassword(value);
      setValidationErrors(errors);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.newPassword || !formData.confirmPassword) {
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setValidationErrors([...validationErrors, 'password.error.mismatch']);
      return;
    }
    const { isValid, errors } = validatePassword(formData.newPassword);
    if (!isValid) {
      setValidationErrors(errors);
      return;
    }
    try {
      await handleResetPassword({
        code: token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      });
      navigate('/new-user-login');
    } catch (err) {
      // Error handling is managed by the hook
    }
  };

  const renderForm = () => (
    <form className="login-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="newPassword">{t('passwordReset.newPassword')}</label>
        <small className="form-field__hint">
          {t('registration.security.requirements')}
        </small>
        <div className="password-input-container">
          <input
            type={showPassword ? "text" : "password"}
            id="newPassword"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            disabled={resetLoading}
          />
          <button 
            type="button" 
            className="show-password-button" 
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? t('loginForm.hidePassword') : t('loginForm.showPassword')}
          >
            <img src={showPasswordIcon} alt="" />
          </button>
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="confirmPassword">{t('passwordReset.confirmPassword')}</label>
        <small className="form-field__hint">
          {t('registration.security.requirements')}
        </small>
        <div className="password-input-container">
          <input
            type={showPassword ? "text" : "password"}
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            disabled={resetLoading}
          />
          <button 
            type="button" 
            className="show-password-button" 
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? t('loginForm.hidePassword') : t('loginForm.showPassword')}
          >
            <img src={showPasswordIcon} alt="" />
          </button>
        </div>
      </div>
      {validationErrors.length > 0 && (
        <div className="error-message">
          <ul>
            {validationErrors.map((error, index) => (
              <li key={index}>{t(error)}</li>
            ))}
          </ul>
        </div>
      )}
      {resetError && <div className="error-message">{resetError}</div>}
      <button 
        type="submit" 
        className={`login-button ${isFormValid ? 'valid' : ''}`} 
        disabled={resetLoading || !isFormValid}
      >
        {resetLoading ? t('passwordReset.updating') : t('passwordReset.submit')}
      </button>
    </form>
  );

  return (
    <div className="new-user-login-page">
      <button className="language-toggle" onClick={toggleLanguage} aria-label={t('nav.changeLanguage')}>
        <img src={languageIcon} alt="" />
        <span>{i18n.language.toUpperCase()}</span>
      </button>
      <div className="content-container">
        <header className="header-auth">
          <p className="welcomeText">{t('authPage.welcomeTo')}</p>
          <img src={accoladeLogo} alt={t('altText.accoladeLogo')} className="accoladeLogo" />
        </header>
        <div className="login-form-container">
          <div className="login-form__header">
            <h2 className="login-form__title">{t('passwordReset.title')}</h2>
            <p className="login-form__description">{t('passwordReset.description')}</p>
          </div>
          {renderForm()}
        </div>
      </div>
      <footer className="footer-auth">
        <img src={brandLogos} alt={t('altText.brandLogos')} className="brandLogos" />
        <Link to="/guest-contact" className="contact-link">
          {t('authPage.needHelp')} <span className="underline-hover">{t('authPage.contactUs')}</span>
        </Link>
      </footer>
    </div>
  );
};
